import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import storeId from './storeId';

export type ExportPhase = 'idle' | 'exporting' | 'complete';
export type ExportFileType = 'pdf' | 'jpg';

export const useExportStore = defineStore(storeId.EXPORT, () => {
  /** 弹窗是否可见 */
  const visible = ref(false);
  /** 当前阶段：idle=隐藏, exporting=导出中, complete=导出完成 */
  const phase = ref<ExportPhase>('idle');
  /** 导出进度百分比 0-100 */
  const percentage = ref(0);
  /** 进度描述文案 */
  const displayMessage = ref('');
  /** 弹窗标题 */
  const title = ref('导出');
  /** 导出文件类型 */
  const exportType = ref<ExportFileType>('pdf');
  /** 导出完成后的文件 Blob */
  const downloadBlob = ref<Blob | null>(null);
  /** 下载文件名 */
  const downloadFilename = ref('');

  /** 是否正在导出中 */
  const isExporting = computed(() => phase.value === 'exporting');
  /** 是否导出完成 */
  const isComplete = computed(() => phase.value === 'complete');

  /** 显示导出中状态 */
  function showExporting(type: ExportFileType) {
    phase.value = 'exporting';
    exportType.value = type;
    percentage.value = 0;
    displayMessage.value = '';
    title.value = '导出';
    downloadBlob.value = null;
    downloadFilename.value = '';
    visible.value = true;
  }

  /** 更新导出进度 */
  function updateProgress(pct: number, message: string) {
    percentage.value = Math.max(0, Math.min(100, pct));
    displayMessage.value = message;
  }

  /** 切换为导出完成状态，存储下载 Blob */
  function showComplete(blob: Blob, filename: string) {
    phase.value = 'complete';
    percentage.value = 100;
    downloadBlob.value = blob;
    downloadFilename.value = filename;
  }

  /** 触发浏览器下载并关闭弹窗 */
  function download() {
    if (!downloadBlob.value) return;
    const url = URL.createObjectURL(downloadBlob.value);
    const link = document.createElement('a');
    link.download = downloadFilename.value;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    close();
  }

  /** 关闭弹窗并重置状态 */
  function close() {
    visible.value = false;
    phase.value = 'idle';
    downloadBlob.value = null;
  }

  return {
    visible,
    phase,
    percentage,
    displayMessage,
    title,
    exportType,
    downloadBlob,
    downloadFilename,
    isExporting,
    isComplete,
    showExporting,
    updateProgress,
    showComplete,
    download,
    close
  };
});
